// rideRecorder.ts
// ===============
//
// Wave 14ca-fix41: Free-Ride-Recording. Der User tippt im Map-View auf "RIDE"
// (auch ohne ausgewaehlten Trail) und startet eine GPS-Aufzeichnung.
//
// Architektur:
//   - Singleton-Bus (analog positionBus, mapBus): kein React-State,
//     extern subscriben + UI-Hooks dranhaengen.
//   - Aktive Recording wird LIVE in IDB-Store `rides` persistiert (id +
//     points-Array + startedAt, endedAt bleibt undefined). Damit ueberlebt
//     ein versehentlich geschlossener Tab das Recording — beim naechsten
//     App-Start finden wir die unfinished ride und koennen "resume" bzw.
//     "discard"-Prompt zeigen.
//   - GPS-Punkte werden bewegungs-/zeit-gefiltert appendet, sonst sammelt
//     ein 1Hz-watchPosition bei Pausenstand 3600 redundante Punkte/Stunde.
//   - Live-Stats (Distanz + climb) werden inkrementell on-the-fly gerechnet.
//
// State machine:
//   idle  ─start()──>  recording  ─stop()──>  idle
//                                        \─> "saved ride id" return
//
// Saved Rides:
//   Wenn `stop()` aufgerufen wird, bekommt die ride einen `endedAt` und
//   bleibt im rides-Store. MyRidesPage liest rides where endedAt != null.
//   `syncedAt` bleibt zunaechst leer — Backend-Sync ist Phase 2.
//
// Combine-Prompt (Wave 14ca-fix41-late):
//   `findRecentlyEndedRide()` returnt die juengste ride deren endedAt
//   <RECENT_END_THRESHOLD_MS her ist UND deren letzter Punkt innerhalb
//   COMBINE_DISTANCE_M von der aktuellen GPS-Position liegt. Caller (MapPage
//   beim Start) zeigt Confirm-Dialog "Combine with previous?"; bei Yes
//   wird statt einer neuen ride die alte fortgesetzt (endedAt wieder
//   unset, neue Punkte appendet).

import { getDb } from './storage/db';
import { positionBus } from './positionBus';
import {
  enqueueRideDelete,
  enqueueRidePush,
  flushRideQueueSoon,
} from './rideSync';

/** Punkt-Form im IDB rides-store. lat/lon mandatory, ele/speed optional,
    t = epoch ms (Geolocation timestamp). */
export interface RidePoint {
  lat: number;
  lon: number;
  ele?: number;
  t: number;
  speed?: number;
}

/** Eine gespeicherte oder laufende Ride. */
export interface Ride {
  id: string;
  startedAt: string; // ISO
  endedAt?: string;  // ISO; undefined = laufend
  points: RidePoint[];
  // Wave 14cg Part 2: cross-device-Sync-Felder. updatedAt wird bei jedem
  // lokalen Mutate gebumpt; syncedAt bei jedem erfolgreichen Server-Push.
  // Ride gilt als "pending sync" wenn syncedAt < updatedAt (oder syncedAt
  // fehlt). Alte Pre-14cg-Rows haben weder updatedAt noch syncedAt — die
  // Queue/Flush-Pipeline zieht sie beim ersten Online-Tick hoch.
  updatedAt?: string;
  syncedAt?: string;
}

/** Live-State waehrend Recording. */
export interface ActiveRideStats {
  rideId: string;
  startedAt: string;
  pointCount: number;
  /** Cumulative distance in meters since start. */
  distanceMeters: number;
  /** Cumulative elevation gain in meters (ascending segments only). */
  elevationGainM: number;
  /** Cumulative elevation loss (descending). */
  elevationLossM: number;
  /** Wave 14cf-fix20: in-progress climb seit dem letzten Hysterese-Pivot
   *  (noch nicht zum committed `elevationGainM` addiert). UI-Layer addiert
   *  diesen Wert für die Live-Anzeige, sodass der User bei monoton
   *  bergauf gefahrenen Ride-Phasen die Höhenmeter inkrementieren sieht.
   *  Sobald die Hysterese einen Climb committed (Reversal um ≥ THRESHOLD),
   *  wandert der Wert in `elevationGainM` und `pendingClimbM` springt
   *  zurück auf 0 — die Summe (committed + pending) bleibt stabil, keine
   *  UI-Flicker. Beim `stop()` werden die Punkte raw persistiert; das
   *  finale Aggregat via `computeStats()` hat einen Tail-Handler der den
   *  noch-offenen Run ohnehin ein letztes Mal verbucht. */
  pendingClimbM: number;
  /** Wave 14cf-fix20: symmetrisch zu `pendingClimbM` für Descent. */
  pendingDescentM: number;
  /** Wave 14cf-fix6: Zeit-in-Bewegung (Millisekunden). Nur Intervalle
   *  zwischen Punkten die ECHTE Bewegung dokumentieren (dist >=
   *  MIN_MOVE_METERS) werden mitgezählt. Pausen, Ampelhalt, Kafee-Stop
   *  bleiben aussen vor. Garmin/Strava nennen das "Moving Time". */
  movingTimeMs: number;
  /** Most recent point — fuer UI-Marker / GPS-Status-Anzeige. */
  lastPoint: RidePoint | null;
}

/** Recording-Status. */
export type RecorderState =
  | { kind: 'idle' }
  | { kind: 'recording'; stats: ActiveRideStats };

type Listener = (state: RecorderState) => void;

// ─────────────────────────────────────────────────────────────────────────────
// Tunables
// ─────────────────────────────────────────────────────────────────────────────

/** Mindest-Bewegung zwischen zwei Punkten — sonst silent drop. Verhindert dass
    ein stehendes Bike (GPS-Drift ~3-5m) die Distanzanzeige hochschnellt. */
const MIN_MOVE_METERS = 4;

/** Max-Zeit zwischen Punkten — wenn der User stehenbleibt, akzeptieren wir
    nach 30s wieder einen Punkt um den Track-Hinweis aktuell zu halten. */
const MAX_GAP_MS = 30_000;

/** Max plausible Speed-Lueck: punkte mit Geschwindigkeit > 100 km/h zwischen
    zwei Samples (= GPS-Glitch / Tunnel-Sprung) verwerfen. */
const MAX_SPEED_MPS = 28; // ~100 km/h

/** Combine-Prompt: ride innerhalb dieses Zeitfensters seit endedAt zaehlt
    als "recent" und wird im Combine-Dialog angeboten. */
export const RECENT_END_THRESHOLD_MS = 30 * 60 * 1000; // 30 min

/** Combine-Prompt: aktuelle Start-Position muss innerhalb dieses Radius vom
    letzten Punkt der vorherigen Ride sein. */
export const COMBINE_DISTANCE_M = 250;

/** Wave 14ca-fix63: Höhenmeter-Deadband (Legacy — wird noch von
    shared/geo.ts:elevationStats für Trail-/Run-Aggregate-Anzeigen
    genutzt). FÜR LIVE-RECORDING ist das Verfahren ab Wave 14cf-fix7 auf
    Peak-to-Peak-Hysterese (s. ELEVATION_HYSTERESIS_THRESHOLD_M) umgestellt.
    Anchor-Deadband akkumuliert auf längeren Tracks selbst bei moderater
    GPS-Noise spürbares Phantom — der User vergleicht mit Apple-Watch
    (Barometer) und sieht systematisch +20–40 % zu viel. */
export const ELEVATION_DEADBAND_M = 4;

/** Wave 14cf-fix7: Hysterese-Threshold für Live-Recording-Höhenmeter.
 *  Garmin/Strava-Standard. Wir tracken laufende Min/Max seit der letzten
 *  bestätigten Extremstelle (pivot); ein Climb wird erst dann committed,
 *  wenn die Höhe um mindestens THRESHOLD vom Peak runter reverst — oder
 *  ein Descent wenn um THRESHOLD vom Tal hoch.
 *
 *  iPhone-GPS hat ±5–10 m Noise pro Sample. 8 m schluckt das zuverlässig,
 *  zählt aber alle echten Climbs ≥ 8 m. Bei dichten Switchbacks (wenige
 *  Meter Höhe pro Stück) verlieren wir Mini-Kicker — aber 8 m sind etwa
 *  zwei Stockwerke; wer 100 m Höhenmeter macht, hat normalerweise nicht
 *  alle in 1-m-Häppchen abgearbeitet, sondern ein paar zusammenhängende
 *  Anstiege.
 *
 *  Tunable. Wenn user-side die Anzeige im Vergleich zur Watch zu niedrig
 *  ist, runter (5 m); wenn zu hoch, hoch (10 m). */
export const ELEVATION_HYSTERESIS_THRESHOLD_M = 8;

// ─────────────────────────────────────────────────────────────────────────────
// Distanz- und Hoehenmeter-Math
// ─────────────────────────────────────────────────────────────────────────────

const EARTH_R = 6371000;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function haversineMeters(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number {
  const phi1 = toRad(aLat);
  const phi2 = toRad(bLat);
  const dphi = toRad(bLat - aLat);
  const dlam = toRad(bLon - aLon);
  const h =
    Math.sin(dphi / 2) ** 2 +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dlam / 2) ** 2;
  return 2 * EARTH_R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

// ─────────────────────────────────────────────────────────────────────────────
// Bus
// ─────────────────────────────────────────────────────────────────────────────

class RideRecorder {
  private state: RecorderState = { kind: 'idle' };
  private listeners = new Set<Listener>();
  /** In-memory copy der points fuer Distance-Math. IDB ist langsam zum
      jedem Append-Read, daher halten wir den Track im Speicher und
      checkpointen alle 5s/N points zur Persistenz. */
  private points: RidePoint[] = [];
  private lastPersistAt = 0;
  /** Wave 14cf-fix7: Peak-to-Peak-Hysterese-State für Höhenmeter.
   *
   *  pivot: zuletzt "gelockte" Extremstelle (von der aus die nächste
   *         Reversal gemessen wird).
   *  runMin/runMax: laufendes Min/Max seit dem letzten Pivot.
   *  direction: -1 = aktuell descending, +1 = ascending, 0 = unbestimmt.
   *
   *  Algo: pro Sample updaten wir runMin/Max. Sobald wir vom runMax um
   *  THRESHOLD runter reversen, gilt der Peak als bestätigt — wir
   *  committen `runMax - pivot` als gain und setzen alles auf v zurück.
   *  Analog für Tal → loss.
   *
   *  Vorteil gegenüber Anchor-Deadband: wir zählen nur ECHTE Reversals,
   *  keine kleinen Vor-und-zurück-Schritte die den Anchor einzeln
   *  treffen. Auf langen Touren mit moderater GPS-Noise sind das
   *  Größenordnungen weniger Phantom-Gain. */
  private hystPivot: number | null = null;
  private hystRunMin = Infinity;
  private hystRunMax = -Infinity;
  private hystDir: -1 | 0 | 1 = 0;

  get(): RecorderState {
    return this.state;
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  /** Wave 14ds: Public-Snapshot des aufgezeichneten Tracks für die
   *  Battery-Safe-Mode-Briefmarke (Breadcrumb-Layer). Kopie zurückgeben,
   *  damit Konsumenten die interne Liste nicht mutieren. Leer wenn kein
   *  Ride aktiv. */
  getPoints(): RidePoint[] {
    return this.points.slice();
  }

  private emit() {
    for (const l of this.listeners) l(this.state);
  }

  /** Startet eine NEUE Ride. Gibt die ride id zurueck.
      Wenn schon eine recording laeuft: silent no-op + return current id. */
  async start(): Promise<string> {
    if (this.state.kind === 'recording') return this.state.stats.rideId;
    const id = `ride_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const startedAt = new Date().toISOString();
    this.points = [];
    // Hysterese-State zurücksetzen — beim ersten valide Ele-Sample
    // wird pivot gesetzt und runMin/Max davon initialisiert.
    this.hystPivot = null;
    this.hystRunMin = Infinity;
    this.hystRunMax = -Infinity;
    this.hystDir = 0;
    const ride: Ride = { id, startedAt, points: [] };
    try {
      const db = await getDb();
      await db.put('rides', ride);
    } catch (err) {
      console.warn('[ride] persist start failed', err);
    }
    this.state = {
      kind: 'recording',
      stats: {
        rideId: id,
        startedAt,
        pointCount: 0,
        distanceMeters: 0,
        elevationGainM: 0,
        elevationLossM: 0,
        pendingClimbM: 0,
        pendingDescentM: 0,
        movingTimeMs: 0,
        lastPoint: null,
      },
    };
    this.emit();
    return id;
  }

  /** Resumed eine bestehende, noch nicht beendete Ride (Combine-Use-Case oder
      App-Restart waehrend Recording). Wirft wenn die ride schon endedAt hat. */
  async resume(rideId: string): Promise<void> {
    const db = await getDb();
    const ride = await db.get('rides', rideId);
    if (!ride) throw new Error('ride_not_found');
    if (ride.endedAt) {
      // Combine-Fall: User will eine bereits beendete ride fortsetzen.
      // Wir setzen endedAt zurueck und appenden weiter.
      await db.put('rides', { ...ride, endedAt: undefined });
    }
    this.points = ride.points.slice();
    const stats = computeStats(this.points);
    // Wave 14cf-fix7: Resume → Hysterese-State aus den Punkten replayen.
    // Wir nutzen replayHysteresis() mit denselben Schwellwerten wie
    // computeStats und übernehmen den End-State, damit der nächste
    // appendPoint nahtlos weiterläuft (kein Doppelzählen, kein verlorenes
    // Inkrement).
    const replay = replayHysteresis(this.points);
    this.hystPivot = replay.pivot;
    this.hystRunMin = replay.runMin;
    this.hystRunMax = replay.runMax;
    this.hystDir = replay.direction;
    // Wave 14cf-fix20: pending-Werte aus dem replayed State herleiten.
    // computeStats() hat in seinem Tail-Handler bereits den letzten
    // offenen Run ZUM committed gain/loss dazugerechnet — d.h. `stats`
    // hier enthält schon das was nach dem letzten Pivot vor dem Resume
    // noch offen war. Damit ist `pending` für die Live-Anzeige bei einem
    // gerade resumierten Ride initial 0; sobald der nächste appendPoint
    // den runMax/runMin wieder bewegt, baut sich pending neu auf.
    this.state = {
      kind: 'recording',
      stats: {
        rideId,
        startedAt: ride.startedAt,
        pointCount: this.points.length,
        distanceMeters: stats.distanceMeters,
        elevationGainM: stats.elevationGainM,
        elevationLossM: stats.elevationLossM,
        pendingClimbM: 0,
        pendingDescentM: 0,
        // Wave 14cf-fix6: bei Resume reconstruieren wir movingTime aus dem
        // Track. computeStats() liefert sie als Teil der Aggregat-Stats.
        movingTimeMs: stats.movingTimeMs,
        lastPoint: this.points[this.points.length - 1] ?? null,
      },
    };
    this.emit();
  }

  /** GPS-Punkt aufnehmen — gefiltert, idempotent gegen Doppel-Calls.
      Caller schickt rohe Geolocation-Coords; wir entscheiden ob's appendet
      wird. Returns true wenn appended, false wenn gedroppt. */
  async appendPoint(p: RidePoint): Promise<boolean> {
    if (this.state.kind !== 'recording') return false;

    const last = this.points[this.points.length - 1];
    if (last) {
      const dist = haversineMeters(last.lat, last.lon, p.lat, p.lon);
      const dt = p.t - last.t;
      // Drop bei Stillstand UND nicht zu lange ohne neuen Punkt.
      if (dist < MIN_MOVE_METERS && dt < MAX_GAP_MS) return false;
      // Drop bei impossible speed (GPS jump).
      if (dt > 0) {
        const speedMps = (dist / dt) * 1000;
        if (speedMps > MAX_SPEED_MPS) {
          console.warn('[ride] GPS jump dropped:', speedMps.toFixed(1), 'm/s');
          return false;
        }
      }
    }

    this.points.push(p);

    // Wave 14ds: GPS-Fix zusätzlich in den positionBus spiegeln. Vorher
    // befüllte nur LocateControl den positionBus — wer im Ride aufzeichnet
    // ohne Locate gedrückt zu haben, hatte positionBus=null → die Battery-
    // Safe-Mode-Briefmarke konnte nicht zentrieren. Jetzt liefert jeder
    // appendPoint die Position. Heading wird aus dem Bearing last→current
    // berechnet (RidePoint speichert keinen heading), damit der Heading-
    // Pfeil in der Briefmarke auch ohne LocateControl funktioniert.
    let heading: number | null = null;
    if (last) {
      const φ1 = (last.lat * Math.PI) / 180;
      const φ2 = (p.lat * Math.PI) / 180;
      const Δλ = ((p.lon - last.lon) * Math.PI) / 180;
      const y = Math.sin(Δλ) * Math.cos(φ2);
      const x =
        Math.cos(φ1) * Math.sin(φ2) -
        Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
      heading = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
    }
    positionBus.set({
      latitude: p.lat,
      longitude: p.lon,
      altitude: p.ele ?? null,
      accuracy: 0,
      altitudeAccuracy: null,
      heading,
      speed: p.speed ?? null,
    } as GeolocationCoordinates);

    // Stats inkrementell rechnen — kein full re-loop ueber alle Punkte.
    const stats = this.state.stats;
    const newStats: ActiveRideStats = {
      ...stats,
      pointCount: stats.pointCount + 1,
      lastPoint: p,
    };
    if (last) {
      const segDist = haversineMeters(last.lat, last.lon, p.lat, p.lon);
      newStats.distanceMeters += segDist;
      // Wave 14cf-fix6: Moving-Time. Wir zählen das Intervall (last → p)
      // nur wenn echte Bewegung stattfand (segDist >= MIN_MOVE_METERS).
      // Damit ist jede Pause/jeder Stop ausgeschlossen: bei Stillstand
      // werden Punkte gar nicht erst angehängt (siehe Filter oben), und
      // ein 30s-Gap-Punkt nach langer Pause hat segDist < MIN_MOVE_METERS
      // → kein Beitrag zur Moving-Time. Konvergent zur Garmin/Strava-
      // Definition.
      if (segDist >= MIN_MOVE_METERS) {
        const segMs = p.t - last.t;
        if (segMs > 0 && segMs < MAX_GAP_MS) {
          newStats.movingTimeMs += segMs;
        }
      }
    }
    // Wave 14cf-fix7: Peak-to-Peak-Hysterese statt Anchor-Deadband.
    // Tracken laufende Min/Max seit letztem Pivot; nur ECHTE Reversals
    // um >= ELEVATION_HYSTERESIS_THRESHOLD_M committen Höhenmeter.
    // Filtert GPS-Noise auch auf langen Tracks zuverlässig — User-Bug:
    // 300 m (Apple Watch) → 400+ m (alter Anchor-Algo, +33 % Phantom).
    if (typeof p.ele === 'number' && Number.isFinite(p.ele)) {
      const v = p.ele;
      if (this.hystPivot == null) {
        // Erstes valides ele → Pivot setzen, runMin/Max initialisieren.
        this.hystPivot = v;
        this.hystRunMin = v;
        this.hystRunMax = v;
      } else {
        if (v > this.hystRunMax) this.hystRunMax = v;
        if (v < this.hystRunMin) this.hystRunMin = v;
        const T = ELEVATION_HYSTERESIS_THRESHOLD_M;
        // Event A: wir kommen vom Peak um THRESHOLD runter → Climb commit.
        if (this.hystDir !== -1 && this.hystRunMax - v >= T) {
          newStats.elevationGainM += this.hystRunMax - this.hystPivot;
          this.hystPivot = this.hystRunMax;
          this.hystDir = -1;
          this.hystRunMin = v;
          this.hystRunMax = v;
        }
        // Event B: wir gehen vom Tal um THRESHOLD hoch → Descent commit.
        else if (this.hystDir !== +1 && v - this.hystRunMin >= T) {
          newStats.elevationLossM += this.hystPivot - this.hystRunMin;
          this.hystPivot = this.hystRunMin;
          this.hystDir = +1;
          this.hystRunMax = v;
          this.hystRunMin = v;
        }
      }
    }
    // Wave 14cf-fix20: pending-Werte aus dem aktuellen Hysterese-State
    // ableiten. Bei monotoner Climb (keine Reversal seit Start) ist
    // hystRunMax > hystPivot und hystRunMin == hystPivot → pendingClimbM
    // > 0, pendingDescentM == 0. Symmetrisch fürs Tal. Direkt nach einem
    // Reversal-Commit ist runMin == runMax == v == neuer pivot → beide
    // pending zurück auf 0, Summe (committed + pending) bleibt stabil.
    if (this.hystPivot != null) {
      newStats.pendingClimbM = Math.max(0, this.hystRunMax - this.hystPivot);
      newStats.pendingDescentM = Math.max(0, this.hystPivot - this.hystRunMin);
    }
    this.state = { kind: 'recording', stats: newStats };
    this.emit();

    // IDB-Checkpoint alle ~5 Punkte oder 10s, je nachdem was zuerst kommt.
    const now = Date.now();
    if (this.points.length % 5 === 0 || now - this.lastPersistAt > 10_000) {
      this.lastPersistAt = now;
      try {
        const db = await getDb();
        const ride = await db.get('rides', stats.rideId);
        if (ride && !ride.endedAt) {
          await db.put('rides', { ...ride, points: this.points.slice() });
        }
      } catch (err) {
        console.warn('[ride] checkpoint failed', err);
      }
    }

    return true;
  }

  /** Beendet die laufende Ride, persistiert den finalen Stand. Returns die
      saved ride id, oder null wenn nichts lief. */
  async stop(): Promise<string | null> {
    if (this.state.kind !== 'recording') return null;
    const id = this.state.stats.rideId;
    const endedAt = new Date().toISOString();
    try {
      const db = await getDb();
      const ride = await db.get('rides', id);
      if (ride) {
        // Wave 14cg Part 2: beim Beenden bumpen wir updatedAt — der
        // Sync-Layer sieht damit "neuer Stand pending push".
        await db.put('rides', {
          ...ride,
          points: this.points.slice(),
          endedAt,
          updatedAt: endedAt,
        });
      }
    } catch (err) {
      console.warn('[ride] stop persist failed', err);
    }
    // Wave 14cg Part 2: Push enqueuen. enqueueRidePush ist idempotent;
    // flushRideQueueSoon versucht den Push sofort (wenn online), sonst
    // bleibt der Eintrag in der Queue bis zum nächsten online-Event.
    try {
      await enqueueRidePush(id);
      flushRideQueueSoon();
    } catch (err) {
      console.warn('[ride] enqueue stop-push failed', err);
    }
    this.state = { kind: 'idle' };
    this.points = [];
    this.emit();
    return id;
  }

  /** Discard waehrend Recording (User bricht ab) — loescht aus IDB. */
  async discard(): Promise<void> {
    if (this.state.kind !== 'recording') return;
    const id = this.state.stats.rideId;
    try {
      const db = await getDb();
      await db.delete('rides', id);
    } catch (err) {
      console.warn('[ride] discard failed', err);
    }
    this.state = { kind: 'idle' };
    this.points = [];
    this.emit();
  }
}

export const rideRecorder = new RideRecorder();

// ─────────────────────────────────────────────────────────────────────────────
// Helpers — full-track stats (z.B. nach resume oder fuer MyRidesPage-Listing)
// ─────────────────────────────────────────────────────────────────────────────

export function computeStats(points: RidePoint[]): {
  distanceMeters: number;
  elevationGainM: number;
  elevationLossM: number;
  durationMs: number;
  /** Wave 14cf-fix6: Moving-Time-Aggregat fuer Resume / MyRides-Listing.
   *  Selbe Definition wie ActiveRideStats.movingTimeMs. */
  movingTimeMs: number;
} {
  let dist = 0;
  let movingMs = 0;
  // Wave 14cf-fix7: Höhen via Hysterese (s. ELEVATION_HYSTERESIS_THRESHOLD_M).
  // Ersetzt den alten Anchor-Deadband-Algo der auf längeren Tracks
  // ~30 % Phantom-Gain produzierte (User-Bug 300 vs 400 m vs Apple Watch).
  let pivot: number | null = null;
  let runMin = Infinity;
  let runMax = -Infinity;
  let dir: -1 | 0 | 1 = 0;
  let gain = 0;
  let loss = 0;
  const T = ELEVATION_HYSTERESIS_THRESHOLD_M;

  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    if (i > 0) {
      const a = points[i - 1]!;
      const segDist = haversineMeters(a.lat, a.lon, p.lat, p.lon);
      dist += segDist;
      if (segDist >= MIN_MOVE_METERS) {
        const segMs = p.t - a.t;
        if (segMs > 0 && segMs < MAX_GAP_MS) {
          movingMs += segMs;
        }
      }
    }
    if (typeof p.ele === 'number' && Number.isFinite(p.ele)) {
      const v = p.ele;
      if (pivot == null) {
        pivot = v;
        runMin = v;
        runMax = v;
      } else {
        if (v > runMax) runMax = v;
        if (v < runMin) runMin = v;
        if (dir !== -1 && runMax - v >= T) {
          gain += runMax - pivot;
          pivot = runMax;
          dir = -1;
          runMin = v;
          runMax = v;
        } else if (dir !== +1 && v - runMin >= T) {
          loss += pivot - runMin;
          pivot = runMin;
          dir = +1;
          runMax = v;
          runMin = v;
        }
      }
    }
  }
  // Tail: was zwischen letztem Pivot und letztem Sample noch offen ist
  // anhängen — sonst geht eine final aufsteigende Climb verloren.
  if (pivot != null && points.length > 0) {
    const lastEle = (() => {
      for (let i = points.length - 1; i >= 0; i--) {
        const e = points[i]!.ele;
        if (typeof e === 'number' && Number.isFinite(e)) return e;
      }
      return null;
    })();
    if (lastEle != null) {
      if (dir === +1 || (dir === 0 && lastEle > pivot)) {
        gain += Math.max(0, lastEle - pivot);
      } else if (dir === -1 || (dir === 0 && lastEle < pivot)) {
        loss += Math.max(0, pivot - lastEle);
      }
    }
  }

  const durationMs =
    points.length > 1 ? points[points.length - 1]!.t - points[0]!.t : 0;
  return {
    distanceMeters: dist,
    elevationGainM: gain,
    elevationLossM: loss,
    durationMs,
    movingTimeMs: movingMs,
  };
}

/**
 * Wave 14cf-fix7: Replay den Hysterese-State aus einem Punkt-Array.
 * Wird von resume() benutzt damit der nächste appendPoint nahtlos
 * weiterläuft. Liefert pivot/runMin/runMax/direction wie sie nach dem
 * letzten Punkt im Track aussehen würden (kein gain/loss zurückgegeben
 * — den hat computeStats schon).
 */
function replayHysteresis(points: RidePoint[]): {
  pivot: number | null;
  runMin: number;
  runMax: number;
  direction: -1 | 0 | 1;
} {
  let pivot: number | null = null;
  let runMin = Infinity;
  let runMax = -Infinity;
  let dir: -1 | 0 | 1 = 0;
  const T = ELEVATION_HYSTERESIS_THRESHOLD_M;
  for (const p of points) {
    if (typeof p.ele !== 'number' || !Number.isFinite(p.ele)) continue;
    const v = p.ele;
    if (pivot == null) {
      pivot = v;
      runMin = v;
      runMax = v;
      continue;
    }
    if (v > runMax) runMax = v;
    if (v < runMin) runMin = v;
    if (dir !== -1 && runMax - v >= T) {
      pivot = runMax;
      dir = -1;
      runMin = v;
      runMax = v;
    } else if (dir !== +1 && v - runMin >= T) {
      pivot = runMin;
      dir = +1;
      runMax = v;
      runMin = v;
    }
  }
  return { pivot, runMin, runMax, direction: dir };
}

// ─────────────────────────────────────────────────────────────────────────────
// Stored rides queries — fuer MyRidesPage und Combine-Prompt
// ─────────────────────────────────────────────────────────────────────────────

/** Alle finished Rides aus IDB, neueste zuerst. */
export async function listEndedRides(): Promise<Ride[]> {
  try {
    const db = await getDb();
    const all = await db.getAll('rides');
    return all
      .filter((r) => !!r.endedAt)
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  } catch (err) {
    console.warn('[ride] list failed', err);
    return [];
  }
}

/** Loescht eine ride aus dem Store. */
export async function deleteRide(id: string): Promise<void> {
  try {
    const db = await getDb();
    await db.delete('rides', id);
  } catch (err) {
    console.warn('[ride] delete failed', err);
  }
  // Wave 14cg Part 2: Soft-Delete-Marker auf den Server pushen, sonst
  // taucht die Ride bei nächstem Pull wieder auf. Wenn die Ride noch nie
  // hochgeladen war (kein server-state existiert), ist der DELETE-Push
  // ein 404 — der flushRideQueue swallowed das als erfolgreich (s. dort).
  try {
    await enqueueRideDelete(id);
    flushRideQueueSoon();
  } catch (err) {
    console.warn('[ride] enqueue delete-push failed', err);
  }
}

/** Findet eine kuerzlich beendete Ride, deren letzter Punkt nahe der
    aktuellen Position ist — Kandidat fuer Combine-Prompt. Returns null
    wenn kein Match. */
export async function findRecentlyEndedRide(
  currentLat: number,
  currentLon: number,
): Promise<Ride | null> {
  try {
    const db = await getDb();
    const all = await db.getAll('rides');
    const now = Date.now();
    for (const r of all.sort((a, b) =>
      (b.endedAt ?? '').localeCompare(a.endedAt ?? ''),
    )) {
      if (!r.endedAt) continue;
      const endedAtMs = new Date(r.endedAt).getTime();
      if (now - endedAtMs > RECENT_END_THRESHOLD_MS) break;
      const lastPoint = r.points[r.points.length - 1];
      if (!lastPoint) continue;
      const dist = haversineMeters(
        lastPoint.lat,
        lastPoint.lon,
        currentLat,
        currentLon,
      );
      if (dist <= COMBINE_DISTANCE_M) return r;
    }
    return null;
  } catch (err) {
    console.warn('[ride] findRecentlyEnded failed', err);
    return null;
  }
}

/** Findet eine unfinished ride aus einem frueheren Browser-Crash / Tab-Close.
    App-Start kann das pruefen und resume oder discard anbieten. */
export async function findUnfinishedRide(): Promise<Ride | null> {
  try {
    const db = await getDb();
    const all = await db.getAll('rides');
    const unfinished = all.filter((r) => !r.endedAt);
    if (unfinished.length === 0) return null;
    return unfinished.sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0]!;
  } catch (err) {
    console.warn('[ride] findUnfinished failed', err);
    return null;
  }
}
